import { h, render, Component, Fragment } from 'preact';
import { useState, useEffect, useMemo } from 'preact/hooks';
import { getLang, langList, translations } from './lang';
import {
  FontInfo,
  loadFont,
  getFontFamily,
  getFontVariationSettings,
  downloadFont,
  parseFontForGlyphs,
  getAxisValue,
} from './services/fontService';
import './styles/main.scss';

interface GlyphItem {
  char: string;
  code: string;
  name: string;
}

let fontsData: FontInfo[] = [];

function showCopyToast(message: string): void {
  const existing = document.querySelector('.copy-toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = 'copy-toast';
  toast.textContent = message;
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 2000);
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    showCopyToast(getLang('COPIED'));
  } catch (err) {
    console.error('Failed to copy:', err);
  }
}

function FontList({
  fonts,
  selectedFont,
  onSelect,
  searchTerm,
  onSearchChange,
  isOpen,
  onClose,
}: {
  fonts: FontInfo[];
  selectedFont: FontInfo | null;
  onSelect: (font: FontInfo) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  isOpen: boolean;
  onClose: () => void;
}) {
  const filteredFonts = useMemo(() => {
    if (!searchTerm) return fonts;
    const term = searchTerm.toLowerCase();
    return fonts.filter((f) => f.name.toLowerCase().includes(term));
  }, [fonts, searchTerm]);

  return (
    <aside class={`font-sidebar ${isOpen ? 'open' : ''}`}>
      <div class="sidebar-header">
        <h1>{getLang('SELECT_FONT')}</h1>
        <input
          type="text"
          class="search-input"
          placeholder={getLang('SEARCH_FONTS')}
          value={searchTerm}
          onInput={(e) => onSearchChange((e.target as HTMLInputElement).value)}
        />
      </div>
      <div class="font-list">
        {filteredFonts.map((font) => (
          <div
            key={font.id}
            class={`font-item ${selectedFont?.id === font.id ? 'active' : ''}`}
            onClick={() => {
              onSelect(font);
              onClose();
            }}
          >
            <div class="font-name">
              {font.name}
              {font.isVariable && <span class="variable-badge">VAR</span>}
            </div>
            <div class="font-meta">
              {font.glyphCount} {getLang('GLYPH_COUNT')}
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}

function AxisControl({
  font,
  axisValues,
  onChange,
}: {
  font: FontInfo;
  axisValues: Record<string, number>;
  onChange: (values: Record<string, number>) => void;
}) {
  if (!font.isVariable || font.axes.length === 0) {
    return null;
  }

  const handleChange = (tag: string, value: number) => {
    onChange({ ...axisValues, [tag]: value });
  };

  const axisTags = font.axes.map((a) => a.tag).join(', ');

  return (
    <div class="axis-section">
      <h4>{getLang('VARIABLE_AXES')}</h4>
      <div class="axis-info">Axes detected: {axisTags}</div>
      {font.axes.map((axis) => (
        <div class="axis-row" key={axis.tag}>
          <span class="axis-name">{axis.name || axis.tag}</span>
          <input
            type="range"
            min={axis.min}
            max={axis.max}
            step={1}
            value={axisValues[axis.tag] ?? axis.default}
            onInput={(e) => handleChange(axis.tag, parseInt((e.target as HTMLInputElement).value))}
          />
          <input
            type="number"
            class="axis-number"
            min={axis.min}
            max={axis.max}
            step={1}
            value={axisValues[axis.tag] ?? axis.default}
            onInput={(e) => handleChange(axis.tag, parseInt((e.target as HTMLInputElement).value))}
          />
          <span class="axis-value">{axisValues[axis.tag] ?? axis.default}</span>
        </div>
      ))}
    </div>
  );
}

function GlyphGrid({
  font,
  fontSize,
  axisValues,
}: {
  font: FontInfo;
  fontSize: number;
  axisValues: Record<string, number>;
}) {
  const [glyphs, setGlyphs] = useState<GlyphItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setGlyphs([]);
    setLoading(true);
    parseFontForGlyphs(font).then((g) => {
      setGlyphs(g);
      setLoading(false);
    });
  }, [font.id]);

  const fontStyle = useMemo(
    () => ({
      fontFamily: getFontFamily(font),
      fontSize: `${fontSize}px`,
      fontVariationSettings: font.isVariable ? getFontVariationSettings(axisValues) : 'normal',
    }),
    [font, fontSize, axisValues]
  );

  if (loading) {
    return <div class="glyph-grid">Loading glyphs...</div>;
  }

  return (
    <div class="glyph-grid">
      {glyphs.map((glyph, idx) => (
        <div
          key={idx}
          class="glyph-item"
          onClick={() => copyToClipboard(glyph.char)}
          title={getLang('CLICK_TO_COPY')}
        >
          <span class="glyph-char" style={fontStyle}>
            {glyph.char}
          </span>
          <span class="glyph-code">{glyph.code}</span>
        </div>
      ))}
    </div>
  );
}

function FontPreview({
  font,
  text,
  onTextChange,
  fontSize,
  onFontSizeChange,
  lineHeight,
  onLineHeightChange,
  textColor,
  onTextColorChange,
  bgColor,
  onBgColorChange,
  showGlyphs,
  onGlyphsToggle,
  axisValues,
  onAxisChange,
}: {
  font: FontInfo | null;
  text: string;
  onTextChange: (text: string) => void;
  fontSize: number;
  onFontSizeChange: (size: number) => void;
  lineHeight: number;
  onLineHeightChange: (height: number) => void;
  textColor: string;
  onTextColorChange: (color: string) => void;
  bgColor: string;
  onBgColorChange: (color: string) => void;
  showGlyphs: boolean;
  onGlyphsToggle: (show: boolean) => void;
  axisValues: Record<string, number>;
  onAxisChange: (values: Record<string, number>) => void;
}) {
  const fontStyle = useMemo(() => {
    if (!font) return {};
    return {
      fontFamily: getFontFamily(font),
      fontSize: `${fontSize}px`,
      lineHeight: `${lineHeight}`,
      color: textColor,
      fontVariationSettings: font.isVariable ? getFontVariationSettings(axisValues) : 'normal',
    };
  }, [font, fontSize, lineHeight, textColor, axisValues]);

  if (!font) {
    return (
      <div class="font-preview">
        <p>Select a font to preview</p>
      </div>
    );
  }

  return (
    <div class="font-main">
      <div class="font-controls">
        <div class="control-group">
          <label>{getLang('SAMPLE_TEXT')}</label>
          <input
            type="text"
            value={text}
            onInput={(e) => onTextChange((e.target as HTMLInputElement).value)}
          />
        </div>

        <div class="control-group">
          <label>{getLang('FONT_SIZE')}</label>
          <input
            type="range"
            min="8"
            max="200"
            value={fontSize}
            onInput={(e) => onFontSizeChange(parseInt((e.target as HTMLInputElement).value))}
          />
          <span class="control-value">{fontSize}px</span>
        </div>

        <div class="control-group">
          <label>Line Height</label>
          <input
            type="range"
            min="0.8"
            max="3"
            step="0.1"
            value={lineHeight}
            onInput={(e) => onLineHeightChange(parseFloat((e.target as HTMLInputElement).value))}
          />
          <span class="control-value">{lineHeight}</span>
        </div>

        <div class="control-group">
          <label>Text</label>
          <input
            type="color"
            value={textColor}
            onInput={(e) => onTextColorChange((e.target as HTMLInputElement).value)}
            class="color-input"
          />
        </div>

        <div class="control-group">
          <label>Background</label>
          <input
            type="color"
            value={bgColor}
            onInput={(e) => onBgColorChange((e.target as HTMLInputElement).value)}
            class="color-input"
          />
          <button
            class="btn btn-secondary btn-small"
            onClick={() => onBgColorChange(bgColor === '#1e293b' ? '#ffffff' : '#1e293b')}
          >
            {bgColor === '#1e293b' ? 'Light' : 'Dark'}
          </button>
        </div>

        <div class="control-group">
          <label class="checkbox-label">
            <input
              type="checkbox"
              checked={showGlyphs}
              onChange={(e) => onGlyphsToggle((e.target as HTMLInputElement).checked)}
            />
            {getLang('SHOW_GLYPHS')}
          </label>
        </div>

        <button class="btn btn-primary" onClick={() => downloadFont(font)}>
          {getLang('DOWNLOAD')}
        </button>

        <button
          class="btn btn-secondary"
          onClick={() => {
            const css = `font-family: ${getFontFamily(font)};`;
            copyToClipboard(css);
          }}
        >
          Copy CSS
        </button>

        <div class="lang-selector">
          {langList.map((lang) => (
            <button
              key={lang}
              class={`lang-btn ${localStorage.getItem('lang') === lang ? 'active' : ''}`}
              onClick={() => {
                localStorage.setItem('lang', lang);
                window.location.reload();
              }}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div class="font-preview" style={{ backgroundColor: bgColor }}>
        <div class="preview-text" style={fontStyle}>
          {text}
        </div>

        {font.isVariable && (
          <AxisControl font={font} axisValues={axisValues} onChange={onAxisChange} />
        )}

        <div class="font-info">
          <div class="info-item">
            <span class="info-label">{getLang('FONT_INFO')}</span>
            <span class="info-value">{font.name}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{getLang('IS_VARIABLE')}</span>
            <span class="info-value">{font.isVariable ? getLang('YES') : getLang('NO')}</span>
          </div>
          <div class="info-item">
            <span class="info-label">{getLang('GLYPH_COUNT')}</span>
            <span class="info-value">{font.glyphCount}</span>
          </div>
        </div>

        {showGlyphs && (
          <div class="glyph-section">
            <h3>{getLang('GLYPHS')}</h3>
            <GlyphGrid font={font} fontSize={32} axisValues={axisValues} />
          </div>
        )}
      </div>
    </div>
  );
}

class FontApp extends Component {
  state = {
    fonts: [] as FontInfo[],
    selectedFont: null as FontInfo | null,
    searchTerm: '',
    text: 'The quick brown fox jumps over the lazy dog',
    fontSize: 48,
    lineHeight: 1.4,
    textColor: '#1e293b',
    bgColor: '#ffffff',
    showGlyphs: false,
    axisValues: {} as Record<string, number>,
    sidebarOpen: false,
    loaded: false,
  };

  async componentDidMount() {
    try {
      const response = await fetch('/font/fonts.json');
      const data = await response.json();
      fontsData = data.fonts;

      const savedFontId = localStorage.getItem('selectedFont');
      let selectedFont = fontsData[0] || null;

      if (savedFontId) {
        const found = fontsData.find((f) => f.id === savedFontId);
        if (found) selectedFont = found;
      }

      if (selectedFont) {
        await loadFont(selectedFont);
        const axisValues: Record<string, number> = {};
        selectedFont.axes.forEach((axis) => {
          axisValues[axis.tag] = axis.default;
        });

        this.setState({
          fonts: fontsData,
          selectedFont,
          axisValues,
          loaded: true,
        });
      } else {
        this.setState({ fonts: fontsData, loaded: true });
      }
    } catch (err) {
      console.error('Failed to load fonts:', err);
      this.setState({ loaded: true });
    }
  }

  handleFontSelect = async (font: FontInfo) => {
    await loadFont(font);
    localStorage.setItem('selectedFont', font.id);

    const axisValues: Record<string, number> = {};
    font.axes.forEach((axis) => {
      axisValues[axis.tag] = axis.default;
    });

    this.setState({
      selectedFont: font,
      axisValues,
      showGlyphs: false,
    });
  };

  render() {
    const {
      fonts,
      selectedFont,
      searchTerm,
      text,
      fontSize,
      lineHeight,
      textColor,
      bgColor,
      showGlyphs,
      axisValues,
      sidebarOpen,
      loaded,
    } = this.state as any;

    if (!loaded) {
      return (
        <div class="font-app">
          <div class="font-preview">Loading...</div>
        </div>
      );
    }

    return (
      <div class="font-app">
        <button
          class="mobile-menu-btn"
          onClick={() => this.setState({ sidebarOpen: !sidebarOpen })}
        >
          ☰
        </button>

        <div
          class={`sidebar-overlay ${sidebarOpen ? '' : 'hidden'}`}
          onClick={() => this.setState({ sidebarOpen: false })}
        />

        <FontList
          fonts={fonts}
          selectedFont={selectedFont}
          onSelect={this.handleFontSelect}
          searchTerm={searchTerm}
          onSearchChange={(term) => this.setState({ searchTerm: term })}
          isOpen={sidebarOpen}
          onClose={() => this.setState({ sidebarOpen: false })}
        />

        <FontPreview
          font={selectedFont}
          text={text}
          onTextChange={(text) => this.setState({ text })}
          fontSize={fontSize}
          onFontSizeChange={(fontSize) => this.setState({ fontSize })}
          lineHeight={lineHeight}
          onLineHeightChange={(lineHeight) => this.setState({ lineHeight })}
          textColor={textColor}
          onTextColorChange={(textColor) => this.setState({ textColor })}
          bgColor={bgColor}
          onBgColorChange={(bgColor) => this.setState({ bgColor })}
          showGlyphs={showGlyphs}
          onGlyphsToggle={(showGlyphs) => this.setState({ showGlyphs })}
          axisValues={axisValues}
          onAxisChange={(axisValues) => this.setState({ axisValues })}
        />

        <div />
      </div>
    );
  }
}

function update(): void {
  const app = document.getElementById('app');
  if (app) app.dispatchEvent(new Event('font-update'));
}

export function FontInit(): void {
  const app = document.getElementById('app');
  if (app) {
    render(<FontApp />, app);
  }
}

(window as unknown as { FontInit: typeof FontInit }).FontInit = FontInit;
